import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { useDashboardContext } from "../../../contexts/dashboard-context";
import { useMessageToState } from "../../../hooks/useMessageToState";

/** Shared state */
const photoZoomingState = proxy({
	isPhotoZooming: false,
	actions: {
		setPhotoZooming: (value: boolean) => {
			photoZoomingState.isPhotoZooming = value;
		},
	},
});

/**
 * Logic to zoom a photo
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function usePhotoZooming({
	// default infinity because executed in a command transaction
	maxReplaceDelay = Infinity,
}: {
	/** When using in a transaction, we can pass Infinity to keep the command replaceable in the undo stack until transaction is closed */
	maxReplaceDelay?: number;
} = {}) {
	const dashboard = useDashboardContext();

	/** An object holding information information for Panning/Zooming a photo in a zone */
	const zoneZoomCropRef = useRef<eXpress.creator.rendering.ZoneZoomCrop | undefined>(undefined);

	const { isPhotoZooming } = useSnapshot(photoZoomingState);

	const start = useCallback(
		function startPhotoZooming({ selectedZone }: { selectedZone: eXpress.core.PhotoLookZone }) {
			photoZoomingState.actions.setPhotoZooming(true);

			// Start a new PhotoZooming
			zoneZoomCropRef.current = dashboard.createZoneZoomCrop(selectedZone, {
				maxReplaceDelay,
			});
		},
		[dashboard, maxReplaceDelay],
	);

	const update = useCallback(function updatePhotoZooming(
		zoomAmount: number,
		zoomOut: boolean,
		pivotInImageSpace?: eXpress.math.Point,
	) {
		//		if (!dashboard.canZoomInOnPhoto(zoneZoomCropRef.current?.zone)) return;

		zoneZoomCropRef.current?.zoomTo(
			zoomAmount,
			zoomOut ? eXpress.core.zoomOut : eXpress.core.zoomIn,
			undefined,
			pivotInImageSpace,
		);
	}, []);

	const end = useCallback(function endPhotoZooming() {
		if (!zoneZoomCropRef.current) return;
		zoneZoomCropRef.current.dispose();
		zoneZoomCropRef.current = undefined;
		photoZoomingState.actions.setPhotoZooming(false);
	}, []);

	const cancel = useCallback(function cancelPhotoZooming() {
		if (!zoneZoomCropRef.current) return;
		zoneZoomCropRef.current.cancel();
		zoneZoomCropRef.current.dispose();
		zoneZoomCropRef.current = undefined;
		photoZoomingState.actions.setPhotoZooming(false);
	}, []);

	const getCanZoomInState = useCallback(
		(editor: eXpress.core.IEditor) => {
			const zone = editor.selection.getCurrentZone();
			const photoLookZone = eXpress.core.isPhotoLookZone(zone) ? zone : undefined;
			return dashboard.canZoomInOnPhoto(photoLookZone);
		},
		[dashboard],
	);

	const canZoomIn = useMessageToState({
		name: "usePhotoZooming",
		getState: getCanZoomInState,
		captureInteractiveMessages: true,
	});

	const getCanZoomOutState = useCallback(
		(editor: eXpress.core.IEditor) => {
			const zone = editor.selection.getCurrentZone();
			const photoLookZone = eXpress.core.isPhotoLookZone(zone) ? zone : undefined;
			return dashboard.canZoomOutOnPhoto(photoLookZone);
		},
		[dashboard],
	);

	const canZoomOut = useMessageToState({
		name: "usePhotoZooming",
		getState: getCanZoomOutState,
		captureInteractiveMessages: true,
	});

	return [
		{ isPhotoZooming, canZoomIn, canZoomOut },
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
